import type { PlayerOptions, PlayerText } from '@mfc/mfc-video-sdk'
import { locales } from '../locales/en'
import { APPSTATE, SECURESTATE, USERSSTATE } from '../state'
import { slugToID } from '../state/internal/users'
import {
    FCVIDEO_RX_CLUB,
    FCVIDEO_RX_GRP,
    FCVIDEO_RX_IDLE,
    FCVIDEO_RX_PVT,
    FCVIDEO_RX_VOY,
    PLATFORM_MFC,
} from './mfcConstants'

export type VideoConfig = PlayerOptions

/**
 * The zoom config every room player starts from. Gestures are off until the
 * viewer opts in via Settings > Zoom; the SDK persists that choice in
 * _vpsettings and it overrides these defaults on later players. Legacy had no
 * video zoom at all.
 *
 * Phones have no scroll wheel, so `wheel: false` there drops the toggle from
 * Settings > Zoom (the SDK only gates the pinch toggle on touch support).
 * `false` rather than `menu: false` so a saved "on" can never enable it
 * silently. The Portrait layout disables zoom entirely on touch devices; see
 * VideoOptions.disableZoom.
 */
export const getZoomConfig = (): PlayerOptions['zoom'] => ({
    wheel: APPSTATE.app.isMobile
        ? false
        : { defaultEnabled: false, menu: true },
    pinch: { defaultEnabled: false, menu: true },
})

/**
 * The app's wording for the SDK's button tooltips. Its chat-overlay button
 * drives our Theater layout and its theater-mode button our FullWindow (see
 * state/internal/videoViewModeControls), so those carry the layout names.
 * Given at construction: setText() also redraws the settings menu.
 */
const playerText: Partial<PlayerText> = {
    fullscreen: locales.chat.enterFullScreen,
    exitFullscreen: locales.chat.exitFullScreen,
    showChatOverlay: locales.chat.enterTheater,
    hideChatOverlay: locales.chat.exitTheater,
    enterTheaterMode: locales.chat.enterFullWindow,
    exitTheaterMode: locales.chat.exitFullWindow,
}

export const generateVideoConfig = (id: number): VideoConfig => {
    const user = USERSSTATE.users[id]
    const self = USERSSTATE.users[slugToID['me']]

    if (!user || !self) return

    const {
        streamType: modelStreamType,
        flags: modelFlags,
        state: modelState,
        isWowzaOBSVideo,
        isOBSExtVideo,
        sessionChannel,
        isTruePrivate,
        privateShow,
        id: modelId,
        isNGVideo,
        groupShow,
        clubShow,
        channel,
        server,
        phase,
    } = user

    const streamType = modelStreamType || 'public'
    const useTkxAuth = isNGVideo || isWowzaOBSVideo || streamType !== 'public'
    const insideClubShow = streamType === 'club'

    const config: VideoConfig = {
        cxid: useTkxAuth
            ? () => String(USERSSTATE.users[slugToID['me']].auth?.cxid ?? '')
            : () => '',
        vidctx: useTkxAuth
            ? () => {
                  const tokens = insideClubShow
                      ? clubShow.ckx_auth
                      : (USERSSTATE.users[slugToID['me']].auth ?? {})
                  return encodeURIComponent(btoa(JSON.stringify(tokens.ctx)))
              }
            : () => '',
        password: useTkxAuth
            ? () => {
                  const tokens = insideClubShow
                      ? clubShow.ckx_auth
                      : (USERSSTATE.users[slugToID['me']].auth ?? {})
                  return tokens.tkx
              }
            : () => APPSTATE.app.passcode || '',
        videoDomain: server ? 'video' + server + '.myfreecams.com' : null,
        imageUrl: 'https://img.mfcimg.com/',
        platformId: PLATFORM_MFC,
        aspectRatio: '16:9',
        phase:
            isOBSExtVideo &&
            (streamType === 'private' || streamType === 'voyeur')
                ? 'p'
                : isOBSExtVideo && streamType === 'group'
                  ? 'g'
                  : isNGVideo
                    ? phase
                    : 'z',
        ckxauth:
            streamType === 'private' || streamType === 'voyeur'
                ? () => privateShow?.ckx_auth?.ctxenc
                : streamType === 'group'
                  ? () => groupShow?.ckx_auth?.ctxenc
                  : () => undefined,
        state:
            streamType === 'club'
                ? FCVIDEO_RX_CLUB
                : streamType === 'private'
                  ? FCVIDEO_RX_PVT
                  : streamType === 'voyeur'
                    ? FCVIDEO_RX_VOY
                    : streamType === 'group'
                      ? FCVIDEO_RX_GRP
                      : FCVIDEO_RX_IDLE,
        allowControls: true,
        controlBarOrder: [
            'VolumePanel',
            'VideoPickerButton',
            'ChatOverlayButton',
            'TheaterModeButton',
            'FullscreenToggle',
        ],
        onlyVolumeControls: false,
        zoom: getZoomConfig(),
        text: playerText,
        modelState,
        modelFlags,
        modelId,
        sessionId: () => USERSSTATE.users[slugToID['me']].session,
        videoId: `video-${modelId}`,
        roomId: streamType === 'public' ? channel : sessionChannel,
        truepvt: isTruePrivate,
        render: false,
        c2c: false,
        // No SDK placard images: the app draws its own poster (PreviewPoster)
        // under the transparent player for away, offline and show states. The
        // SDK still tears the stream down and marks mfcvs-poster-showing.
        placards: false,
        pvtsession:
            streamType === 'private' || streamType === 'voyeur'
                ? privateShow?.sessionID
                : 0,
        grpsession: streamType === 'group' ? groupShow?.sessionID : 0,
        clbsession: insideClubShow ? clubShow.tksid : 0,
        snaps_token: () =>
            SECURESTATE.imageSecurityConfig.snaps.token || undefined,
        // logFn: m => {
        //     console.log('VideoPlayerJS:', m)
        // },
    }

    return config
}
